import ProfilePictureEditor from "./ProfilePictureEditor";
import UserAvatar from "./UserAvatar";
import fetch from '../utils/authenticatedFetch';
import ModalLayer from './ModalLayer';
import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FaChevronDown, FaChevronRight, FaEye, FaEyeSlash } from 'react-icons/fa';
import './UserProfile.css';

const EyeIcon = FaEye as React.ElementType;
const EyeSlashIcon = FaEyeSlash as React.ElementType;

interface ProfileData {
  name: string;
  surname: string;
  title: string;
  specialization: string;
  gender: string;
  nationalId: string;
  dateOfBirth: string;
  nationality: string;
  highestLevelOfEducation: string;
  contactNumber: string;
  email: string;
  userType: string;
  academicQualifications: string;
  registrationNo: string;
  faculty: string;
  department: string;
}

const USER_TYPE_LABELS: Record<string, string> = {
  ADMIN_CYBER: 'System Admin',
  RESEARCH_CHAIR: 'Research Ethics Committee Chair',
  PROFESSOR: 'Professor',
  RESEARCH_OFFICER: 'Research Officer',
  FACULTY_RAP: 'Faculty Representative',
  FACULTY_ADMIN: 'Faculty Admin',
  INSTITUTE_REPRESENTATIVE: 'Institute Representative',
  DEPARTMENT: 'Department Representative',
  APPLICANT: 'Applicant',
  ADMIN: 'Executive Director RID',
  REVIEWER: 'Research Ethics Committee Secretary',
};

const getUserTypeLabel = (userType?: string | null) =>
  userType ? USER_TYPE_LABELS[String(userType).toUpperCase()] || userType : '';

const UserProfile: React.FC = () => {
  const { account } = useAuth();
  // Extract userId from context (casting to any for safety)
  const userId = (account as any)?.id;

  // Profile state and status messages
  const [profile, setProfile] = useState<ProfileData>({
    name: '',
    surname: '',
    title: '',
    specialization: '',
    gender: '',
    nationalId: '',
    dateOfBirth: '',
    nationality: '',
    highestLevelOfEducation: '',
    contactNumber: '',
    email: '',
    userType: '',
    academicQualifications: '',
    registrationNo: '',
    faculty: '',
    department: ''
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');

  // State for change password modal and its fields
  const [passwordModalVisible, setPasswordModalVisible] = useState<boolean>(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState<string>('');

  // States for toggling password visibility in modal
  const [showOld, setShowOld] = useState<boolean>(false);
  const [showNew, setShowNew] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  // States for collapsible sections
  const [personalOpen, setPersonalOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [academicOpen, setAcademicOpen] = useState(false);

  // Fetch the logged in user's profile using the userId from context.
  useEffect(() => {
    if (!userId) return;
    const fetchProfile = async () => {
      try {
        const res = await fetch(`/v1/api/users/details/${userId}`);
        const data = await res.json();
        if (data.success) {
          const userData = data.data;
          setProfile({
            name: userData.name || '',
            surname: userData.surname || '',
            title: userData.title || '',
            specialization: userData.specialization || '',
            gender: userData.gender || '',
            nationalId: userData.nationalId || '',
            dateOfBirth: userData.dateOfBirth || '',
            nationality: userData.nationality || '',
            highestLevelOfEducation: userData.highestLevelOfEducation || '',
            contactNumber: userData.contactNumber || '',
            email: userData.email || '',
            userType: userData.userType || '',
            academicQualifications: userData.academicQualifications || '',
            registrationNo: userData.registrationNo || '',
            faculty: userData.faculty || '',
            department: userData.department || ''
          });
        } else {
          setError(data.message || 'Error fetching profile');
        }
      } catch (err) {
        setError('Error fetching profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  if (!account || !userId) {
    return <div>Loading...</div>;
  }

  const handleProfileChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`/v1/api/users/edit/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile)
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg('Profile updated successfully');
        setError('');
      } else {
        setError(data.message || 'Error updating profile');
      }
    } catch (err) {
      setError('Error updating profile');
    }
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    try {
      const res = await fetch(`/v1/api/edit-password/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword
        })
      });
      if (res.ok) {
        alert('You have successfully updated your password');
        setPasswordModalVisible(false);
        setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordError('');
      } else {
        setPasswordError('Failed to update password');
      }
    } catch (err) {
      setPasswordError('Error updating password');
    }
  };

  return (
    <div className="prof-container">
      <h2 className="prof-title">My Profile</h2>
      <ProfilePictureEditor id={userId} name={`${profile.name} ${profile.surname}`}/>
      <div className="profile-summary">
        <UserAvatar userId={userId} name={`${profile.name} ${profile.surname}`} size={64}/>
        <div><h3>{[profile.name, profile.surname].filter(Boolean).join(' ') || 'Your account'}</h3>
          <p>{profile.email}</p><span className="profile-role-badge">{profile.userType.replace(/_/g, ' ')}</span></div>
      </div>
      <hr className="prof-separator" />
      {loading ? (
        <p>Loading profile...</p>
      ) : error ? (
        <p className="prof-error">{error}</p>
      ) : (
        <form className="prof-form" onSubmit={handleProfileSubmit}>
          {/* Personal Details Section */}
          <div className="prof-section">
            <div className="prof-section-header" onClick={() => setPersonalOpen(!personalOpen)}>
              <h3 className="prof-section-title">Personal Details</h3>
              <button type="button" className="prof-section-toggle">
                {personalOpen ? <FaChevronDown /> : <FaChevronRight />}
              </button>
            </div>
            {personalOpen && (
              <div className="prof-section-content">
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">Name</label>
                    <input aria-label="name" type="text" name="name" value={profile.name} onChange={handleProfileChange} />
                  </div>
                  <div className="prof-field">
                    <label className="prof-label">Surname</label>
                    <input aria-label="surname" type="text" name="surname" value={profile.surname} onChange={handleProfileChange} />
                  </div>
                </div>
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">Title</label>
                    <input aria-label="title" type="text" name="title" value={profile.title} onChange={handleProfileChange} />
                  </div>
                  <div className="prof-field">
                    <label className="prof-label">Specialization</label>
                    <input aria-label="specialization"
                      type="text"
                      name="specialization"
                      value={profile.specialization}
                      onChange={handleProfileChange}
                    />
                  </div>
                </div>
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">Gender</label>
                    <select aria-label="gender" name="gender" value={profile.gender} onChange={handleProfileChange}>
                      <option value="">--Select--</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="prof-field">
                    <label className="prof-label">National ID</label>
                    <input aria-label="national Id" type="text" name="nationalId" value={profile.nationalId} onChange={handleProfileChange} />
                  </div>
                </div>
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">Date of Birth</label>
                    <input aria-label="date Of Birth" type="date" name="dateOfBirth" value={profile.dateOfBirth} onChange={handleProfileChange} />
                  </div>
                  <div className="prof-field">
                    <label className="prof-label">Nationality</label>
                    <input aria-label="nationality" type="text" name="nationality" value={profile.nationality} onChange={handleProfileChange} />
                  </div>
                </div>
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">Highest Level of Education</label>
                    <input aria-label="highest Level Of Education" type="text" name="highestLevelOfEducation" value={profile.highestLevelOfEducation} onChange={handleProfileChange} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Contact Details Section */}
          <div className="prof-section">
            <div className="prof-section-header" onClick={() => setContactOpen(!contactOpen)}>
              <h3 className="prof-section-title">Contact Details</h3>
              <button type="button" className="prof-section-toggle">
                {contactOpen ? <FaChevronDown /> : <FaChevronRight />}
              </button>
            </div>
            {contactOpen && (
              <div className="prof-section-content">
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">Contact Number</label>
                    <input aria-label="contact Number" type="text" name="contactNumber" value={profile.contactNumber} onChange={handleProfileChange} />
                  </div>
                  <div className="prof-field">
                    <label className="prof-label">Email</label>
                    <input aria-label="email" type="email" name="email" value={profile.email} onChange={handleProfileChange} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Academic Details Section */}
          <div className="prof-section">
            <div className="prof-section-header" onClick={() => setAcademicOpen(!academicOpen)}>
              <h3 className="prof-section-title">Academic Details</h3>
              <button type="button" className="prof-section-toggle">
                {academicOpen ? <FaChevronDown /> : <FaChevronRight />}
              </button>
            </div>
            {academicOpen && (
              <div className="prof-section-content">
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">User Type</label>
                    <input aria-label="user Type" type="text" name="userType" value={getUserTypeLabel(profile.userType)} readOnly />
                  </div>
                  <div className="prof-field">
                    <label className="prof-label">Academic Qualifications</label>
                    <input aria-label="academic Qualifications" type="text" name="academicQualifications" value={profile.academicQualifications} onChange={handleProfileChange} />
                  </div>
                </div>
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">Registration No</label>
                    <input aria-label="registration No" type="text" name="registrationNo" value={profile.registrationNo} onChange={handleProfileChange} />
                  </div>
                </div>
                <div className="prof-form-row">
                  <div className="prof-field">
                    <label className="prof-label">Faculty</label>
                    <input aria-label="faculty" type="text" name="faculty" value={profile.faculty} onChange={handleProfileChange} />
                  </div>
                  <div className="prof-field">
                    <label className="prof-label">Department</label>
                    <input aria-label="department" type="text" name="department" value={profile.department} onChange={handleProfileChange} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <button type="submit" className="prof-save-btn">Save Changes</button>
          {successMsg && <p className="prof-success">{successMsg}</p>}
        </form>
      )}
      <button className="prof-password-btn" onClick={() => setPasswordModalVisible(true)}>
        Change Password
      </button>
      {passwordModalVisible && (
        <ModalLayer className="prof-modal-overlay">
          <div className="prof-modal">
            <h3 className="prof-modal-title">Change Password</h3>
            <form onSubmit={handlePasswordSubmit}>
              <div className="prof-field password-field">
                <label className="prof-label">Old Password</label>
                <div className="prof-password-wrapper">
                  <input aria-label="old Password"
                    type={showOld ? 'text' : 'password'}
                    name="oldPassword"
                    value={passwordData.oldPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <span className="prof-password-toggle" onClick={() => setShowOld(!showOld)}>
                    {showOld ? <EyeSlashIcon /> : <EyeIcon />}
                  </span>
                </div>
              </div>
              <div className="prof-field password-field">
                <label className="prof-label">New Password</label>
                <div className="prof-password-wrapper">
                  <input aria-label="new Password"
                    type={showNew ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <span className="prof-password-toggle" onClick={() => setShowNew(!showNew)}>
                    {showNew ? <EyeSlashIcon /> : <EyeIcon />}
                  </span>
                </div>
              </div>
              <div className="prof-field password-field">
                <label className="prof-label">Confirm Password</label>
                <div className="prof-password-wrapper">
                  <input aria-label="confirm Password"
                    type={showConfirm ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <span className="prof-password-toggle" onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? <EyeSlashIcon /> : <EyeIcon />}
                  </span>
                </div>
              </div>
              {passwordError && <p className="prof-error">{passwordError}</p>}
              <div className="prof-modal-actions">
                <button type="submit" className="prof-modal-btn">Update Password</button>
                <button
                  type="button"
                  className="prof-modal-btn prof-cancel-btn"
                  onClick={() => {
                    setPasswordModalVisible(false);
                    setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordError('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </ModalLayer>
      )}
    </div>
  );
};

export default UserProfile;
